// Tax: each sales tax's return for a month, worked out from the posted lines (collected less the
// credits on purchases), with its rounding shown. Filing locks the figures; the remittance posts
// the payment out of the bank.

import { useState } from 'react';
import { newId } from '../shared/ids.ts';
import type { BooksProps } from './props.ts';
import { monthName, monthOf, parseMoney, previousMonth, todayIn } from './model.ts';
import type { BankAccount, TaxReturn } from './types.ts';
import { Empty, Loading, Money, Panel, useAct, useQuery } from './ui.tsx';

import { useSalesTaxCodes } from './tax-codes.ts';

export { useSalesTaxCodes };

function ReturnCard({
  code,
  start,
  end,
  banks,
  toast,
}: {
  code: string;
  start: string;
  end: string;
  banks: BankAccount[];
  toast: (m: string) => void;
}) {
  const r = useQuery<TaxReturn>('tax.computeReturn', { taxCode: code, periodStart: start, periodEnd: end });
  const act = useAct(toast, () => r.reload());
  const [confirmation, setConfirmation] = useState('');
  const [amount, setAmount] = useState('');
  const [bankAccountId, setBank] = useState(banks[0]?.id ?? '');
  if (!r.data)
    return (
      <Loading
        error={r.error}
        what={`${code} for ${monthName(start)}`}
        kind="table"
        cols={['Line', 'Amount']}
        retry={() => void r.reload()}
      />
    );
  const f = r.data.figures;
  const filed = r.data.filed;
  const owing = filed ? f.netCents - filed.remittedCents : f.netCents;
  return (
    <Panel
      title={`${code} · ${monthName(start)}`}
      aside={
        // a month with no taxed postings owes nothing: it doesn't read as something left undone
        <span className={`chip say ${filed ? 'ok' : f.entryCount === 0 ? '' : 'warn'}`}>
          {filed ? 'Filed' : f.entryCount === 0 ? 'Nothing to file' : 'Not filed'}
        </span>
      }
    >
      <dl className="kv">
        <dt>Taxable sales</dt>
        <dd>
          <Money cents={f.salesCents} />
        </dd>
        <dt>Collected</dt>
        <dd>
          <Money cents={f.collectedCents} />
        </dd>
        {code !== 'PST' && (
          <>
            <dt>Input tax credits</dt>
            <dd>
              <Money cents={-f.itcCents} />
            </dd>
          </>
        )}
        <dt>
          <strong>Net owing</strong>
        </dt>
        <dd>
          <strong>
            <Money cents={f.netCents} />
          </strong>
        </dd>
        {f.roundingCents !== 0 && (
          <>
            <dt className="muted">Rounding on the lines</dt>
            <dd className="muted">
              <Money cents={f.roundingCents} />
            </dd>
          </>
        )}
      </dl>
      <p className="muted small">
        From {f.entryCount} posted entr{f.entryCount === 1 ? 'y' : 'ies'}.
      </p>
      {!filed ? (
        <div className="fgrid inline-form">
          <label className="full">
            <span className="flabel">Confirmation number</span>
            <input
              className="field"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              placeholder="From the filing site (optional)"
            />
          </label>
          <div className="row-actions">
            <button
              className="btn primary"
              disabled={act.busy}
              onClick={() =>
                void act.run(
                  'tax.fileReturn',
                  {
                    taxCode: code,
                    periodStart: start,
                    periodEnd: end,
                    ...(confirmation ? { confirmation } : {}),
                  },
                  `${code} for ${monthName(start)} filed.`,
                )
              }
            >
              File the return
            </button>
          </div>
        </div>
      ) : owing > 0 ? (
        <div className="fgrid inline-form">
          <label>
            <span className="flabel">Pay from</span>
            <select className="field" value={bankAccountId} onChange={(e) => setBank(e.target.value)}>
              {banks.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="flabel">Amount</span>
            <input
              className="field"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={(owing / 100).toFixed(2)}
            />
          </label>
          <div className="row-actions">
            <button
              className="btn primary"
              disabled={act.busy || !bankAccountId}
              onClick={() =>
                void act.run(
                  'tax.recordRemittance',
                  {
                    remittanceId: newId(),
                    returnId: filed.id,
                    amountCents: parseMoney(amount) ?? owing,
                    paidOn: todayIn(),
                    bankAccountId,
                  },
                  'Remittance posted.',
                )
              }
            >
              Record the payment
            </button>
          </div>
        </div>
      ) : (
        <p className="ok-text small">Filed and paid.</p>
      )}
    </Panel>
  );
}

export function Tax({ toast }: BooksProps) {
  const codes = useSalesTaxCodes();
  const today = todayIn();
  const months = [previousMonth(today), monthOf(today)];
  const [m, setM] = useState(0);
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  const [start, end] = months[m]!;
  return (
    <div className="books-page">
      <div className="page-head">
        <div className="seg" role="group" aria-label="Month">
          {months.map(([s], i) => (
            <button key={s} className={`btn quiet${m === i ? ' on' : ''}`} onClick={() => setM(i)}>
              {monthName(s)}
            </button>
          ))}
        </div>
      </div>
      {!banks.data || !codes ? (
        <Loading
          error={banks.error}
          what={`${monthName(start)}’s returns`}
          retry={() => void banks.reload()}
        />
      ) : !codes.length ? (
        <Empty>
          No sales tax is set up yet, so there are no returns. The owner sets it up under Company → Getting
          started.
        </Empty>
      ) : (
        <div className="split even">
          {codes.map((c) => (
            <ReturnCard
              key={`${c}${start}`}
              code={c}
              start={start}
              end={end}
              banks={banks.data!.bankAccounts}
              toast={toast}
            />
          ))}
        </div>
      )}
    </div>
  );
}
